import React, { useRef } from 'react';

interface BetaCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  tabIndex?: number;
  id?: string;
}

function parseParts(val: string): [string, string] {
  if (!val) return ['', ''];
  const upper = val.toUpperCase();
  const parts = upper.split('-');
  if (parts.length >= 3 && parts[0] === 'BETA') {
    return [(parts[1] ?? '').slice(0, 4), (parts[2] ?? '').slice(0, 4)];
  }
  if (parts.length >= 2) {
    return [(parts[0] ?? '').slice(0, 4), (parts[1] ?? '').slice(0, 4)];
  }
  return [upper.slice(0, 4), ''];
}

function sanitize(raw: string): string {
  return raw.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 4);
}

export function BetaCodeInput({ value, onChange, disabled, tabIndex, id }: BetaCodeInputProps) {
  const [part1, part2] = parseParts(value);
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);

  const buildValue = (p1: string, p2: string): string => {
    if (!p1 && !p2) return '';
    return `BETA-${p1}-${p2}`;
  };

  const handleChange1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clean = sanitize(e.target.value);
    if (clean.length === 4) {
      ref2.current?.focus();
      ref2.current?.select();
    }
    onChange(buildValue(clean, part2));
  };

  const handleChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clean = sanitize(e.target.value);
    onChange(buildValue(part1, clean));
  };

  const handleKeyDown1 = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Nach 4 Zeichen bei Enter/Tab zum nächsten Feld
    if ((e.key === 'ArrowRight' || e.key === 'Tab') && part1.length === 4 && !e.shiftKey) {
      e.preventDefault();
      ref2.current?.focus();
      ref2.current?.select();
    }
  };

  const handleKeyDown2 = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Zurück zum ersten Feld wenn Backspace und Feld leer
    if (e.key === 'Backspace' && part2 === '') {
      ref1.current?.focus();
    }
    if (e.key === 'ArrowLeft' && (ref2.current?.selectionStart ?? 0) === 0) {
      ref1.current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').toUpperCase().replace(/\s/g, '');

    // Vollständigen Code: BETA-XXXX-XXXX oder BETA XXXX XXXX
    const [p1, p2] = parseParts(pasted);

    if (p1 || p2) {
      onChange(buildValue(p1, p2));
      if (p1.length === 4) {
        ref2.current?.focus();
      } else {
        ref1.current?.focus();
      }
      return;
    }

    // Nur 8 Zeichen ohne Bindestriche (XXXXXXXX)
    const stripped = pasted.replace(/-/g, '');
    if (stripped.length > 0) {
      const p1 = sanitize(stripped.slice(0, 4));
      const p2 = sanitize(stripped.slice(4, 8));
      onChange(buildValue(p1, p2));
      if (p1.length === 4) {
        ref2.current?.focus();
      } else {
        ref1.current?.focus();
      }
    }
  };

  const segmentClass =
    'w-[4.5rem] text-center font-mono tracking-widest ' +
    'bg-gray-50 dark:bg-gray-800 ' +
    'border border-gray-300 dark:border-gray-600 rounded-lg ' +
    'px-2 py-2 text-gray-900 dark:text-white text-sm ' +
    'focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ' +
    'disabled:opacity-50 disabled:cursor-not-allowed uppercase placeholder-gray-400 dark:placeholder-gray-600';

  return (
    <div className="flex items-center gap-1.5">
      {/* BETA (readonly) */}
      <div className="flex items-center justify-center w-[4.5rem] h-[38px] font-mono text-sm tracking-widest bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 select-none">
        BETA
      </div>
      <span className="text-gray-400 dark:text-gray-500 font-mono font-bold">-</span>

      {/* Teil 1: 4 Zeichen */}
      <input
        ref={ref1}
        id={id}
        type="text"
        inputMode="text"
        maxLength={4}
        placeholder="XXXX"
        value={part1}
        onChange={handleChange1}
        onKeyDown={handleKeyDown1}
        onPaste={handlePaste}
        disabled={disabled}
        tabIndex={tabIndex}
        className={segmentClass}
        autoCapitalize="characters"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
      />
      <span className="text-gray-400 dark:text-gray-500 font-mono font-bold">-</span>

      {/* Teil 2: 4 Zeichen */}
      <input
        ref={ref2}
        type="text"
        inputMode="text"
        maxLength={4}
        placeholder="XXXX"
        value={part2}
        onChange={handleChange2}
        onKeyDown={handleKeyDown2}
        onPaste={handlePaste}
        disabled={disabled}
        tabIndex={tabIndex}
        className={segmentClass}
        autoCapitalize="characters"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}
